/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import cx from 'classnames';

import { GlossIcon } from 'glossDS/Icons/GlossIcon/GlossIcon';

import { SubNavItem } from 'hooks/navigation/useNavigation';

import s from './Navigation.module.css';

type NavItemProps = {
  to?: string;
  className?: string;
  children: React.ReactNode;
  newTab?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
  subNavItems?: SubNavItem[];
} & (
  | { subNavItems: SubNavItem[]; to?: never }
  | { subNavItems?: never; to: string }
);

const NavItem = ({
  children,
  to,
  className,
  newTab,
  onClick,
  ariaLabel,
  subNavItems,
}: NavItemProps) => {
  const { route } = useRouter();

  const isCurrent = to ? route === to : false;

  if (subNavItems) {
    return (
      <li className={cx(s.navItem, s.hasSubNav, className)}>
        <span role="button" tabIndex={0}>
          {children}
          <GlossIcon
            icon="arrow-down-s-line"
            className={s.subNavArrow}
            size={20}
          />
        </span>
        <ul className={s.subNav}>
          {subNavItems.map((item) => (
            <li key={item.to} className={s.subNavItem}>
              <Link
                href={item.to}
                onClick={item.onClick}
                className={cx({
                  [s.navItemCurrentPage]: route === item.to,
                })}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className={cx(s.navItem, className)}>
      {isCurrent && <div className={s.navItemCurrent} />}
      {newTab ? (
        <a
          href={to}
          target="_blank"
          rel="noreferrer"
          {...(ariaLabel ? { ariaLabel } : {})}
        >
          {children}
        </a>
      ) : (
        <Link href={to} onClick={onClick} aria-label={ariaLabel}>
          {children}
          {isCurrent ? (
            <span className={s.navItemCurrentA11yHidden}>
              {' -  Current location'}
            </span>
          ) : null}
        </Link>
      )}
    </li>
  );
};

export { NavItem };
